import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { missionApi } from "services/brm/system-service";
import handleApi from "services/brm/api-util";
import { projectIdState } from "atoms/atoms-admin";
import { useRecoilValue } from "recoil";
import ButtonCell from "components/EditComponents/ButtonCell";
import { ElementFullDetails, SystemApi } from "features/brm";
import { TYPE } from "constants/brm";
import * as ReactQuery from "@tanstack/react-query";
import * as SysFactsApi from "brm/system-model/system-facts/api";
import { useRoles } from "features/brm/hooks/useRoles";

import {
  createColumnBooleanMapped,
  createColumnActions,
  createColumnCategoryMapped,
} from "brm/tables/services/column/columnFactory";
import { LoadingSpinner as Loading } from "components/elements";
import { RoutePath } from "routes/route-paths";
import MissionDetails from "../details/MissionDetails";

function MissionFullDetails() {
  const { isSystemEngineer, isRiskAnalyst } = useRoles();

  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = useRecoilValue(projectIdState);
  const localProjectId = useRef(projectId);

  const queryClient = ReactQuery.useQueryClient();
  const { data: mission } = SysFactsApi.useMission({
    missionId: id,
    config: { enabled: isRiskAnalyst || isSystemEngineer },
  });
  const { data: elementCats } = SystemApi.useElementCategories({
    projectId,
    options: { enabled: isRiskAnalyst || isSystemEngineer },
  });

  const [selectedElement, setSelectedElement] = useState({});
  const [refresh, setRefresh] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState("");
  const updatedCategory = useRef("");
  const updatedIsInternal = useRef("");

  const createButton = useCallback(
    (cellProps) => {
      return (
        <ButtonCell
          selectedRowId={selectedRowId}
          elementId={cellProps.cell.row.original.id}
          handleConfirmEditClick={async () => {
            setEditMode(false);

            if (updatedIsInternal.current !== "") {
              await handleApi(
                missionApi.setMissionIsInternalWithHttpInfo(selectedRowId, {
                  body: updatedIsInternal.current,
                })
              );
              updatedIsInternal.current = "";
            }

            if (updatedCategory.current !== "") {
              await handleApi(
                missionApi.setMissionCategoryWithHttpInfo(selectedRowId, {
                  body: updatedCategory.current,
                })
              );
              updatedCategory.current = "";
            }

            setRefresh(true);
            queryClient.invalidateQueries(["mission", id]);
          }}
          setEditMode={setEditMode}
          editMode={editMode}
        />
      );
    },

    [selectedRowId, editMode, queryClient, id]
  );

  const columns = useMemo(() => {
    if (isSystemEngineer) {
      return [
        createColumnBooleanMapped("isInternal", selectedRowId, editMode, updatedIsInternal),
        createColumnCategoryMapped("category", selectedRowId, elementCats, editMode, updatedCategory),
        createColumnActions(createButton, { disableFilters: true }),
      ];
    }

    return [
      createColumnBooleanMapped("isInternal", selectedRowId, false, null),
      createColumnCategoryMapped("category", selectedRowId, elementCats, false, null),
    ];
  }, [isSystemEngineer, selectedRowId, elementCats, editMode, createButton]);

  useEffect(() => {
    if (projectId !== localProjectId.current) {
      navigate(RoutePath.Missions);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (mission) {
      setSelectedElement(mission);
    }
  }, [mission]);

  if (mission && selectedElement) {
    const detailsComp = <MissionDetails selectedElement={mission} setRefresh={setRefresh} refresh={refresh} />;

    return (
      <ElementFullDetails
        showBackLink
        data={[mission]}
        columns={columns}
        selectedElement={mission}
        setSelectedElement={setSelectedElement}
        setSelectedRowId={setSelectedRowId}
        detailsComp={detailsComp}
        compName={TYPE.mission}
      />
    );
  }
  return <Loading />;
}

export default MissionFullDetails;
